import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert, ChevronDown, ChevronRight } from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useStoreMapping } from '@/hooks/useStoreMapping';

const DECISIONS = [
  { value: 'HEADER_STORE', label: 'Movimentar na loja do documento', hint: 'Recomendado' },
  { value: 'LINE_STORE', label: 'Movimentar na loja indicada na linha', hint: 'A loja tem de estar mapeada' },
  { value: 'NO_MOVEMENT', label: 'Sem movimento de stock', hint: 'Stock acertado à mão no SAGE' },
];

const DecisionForm = ({ doc, onDone }) => {
  const [decision, setDecision] = useState(doc.proposed_decision || 'HEADER_STORE');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error('Indique o motivo da decisão'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/wintouch/quarantine/${encodeURIComponent(doc.document_key)}/decide`, { decision, reason });
      toast.success(res.data.batch_ids?.length ? `Decisão registada. Movimento criado: ${res.data.batch_ids.join(', ')}` : 'Decisão registada (sem movimento)');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao registar decisão');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-3 pt-3 border-t" data-testid={`quarantine-form-${doc.document_key}`}>
      <div className="grid md:grid-cols-3 gap-2">
        {DECISIONS.map((d) => (
          <label key={d.value} className={`cursor-pointer rounded-lg border p-3 text-sm ${decision === d.value ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}>
            <input type="radio" className="mr-2" checked={decision === d.value} onChange={() => setDecision(d.value)} data-testid={`quarantine-decision-${d.value}`} />
            <span className="font-medium">{d.label}</span>
            <div className="text-xs text-slate-500 ml-5">{d.hint}</div>
          </label>
        ))}
      </div>
      <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo (obrigatório)" rows={2} data-testid="quarantine-reason" />
      <Button onClick={submit} disabled={saving} data-testid="quarantine-submit">Confirmar decisão</Button>
    </div>
  );
};

const QuarantineRow = ({ doc, onDone, getStoreName }) => {
  const [open, setOpen] = useState(false);
  const others = [...new Set((doc.mismatches || []).map((m) => m.row_store))];
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`quarantine-row-${doc.document_key}`}>
      <button className="w-full flex items-center justify-between text-left" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-3">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <div>
            <div className="font-medium">{getStoreName(doc.store_code)} · {doc.business_date} · <span className="font-mono text-sm">{doc.document?.document_type} {doc.document?.series}/{doc.document?.number}</span></div>
            <div className="text-xs text-slate-500">{doc.mismatches?.length} linha(s) com loja {others.join(', ')} · {doc.document?.lines?.length} linha(s) no documento</div>
          </div>
        </div>
        {doc.status === 'PENDING'
          ? <Badge className="bg-amber-100 text-amber-800">Por decidir</Badge>
          : <Badge className="bg-emerald-100 text-emerald-800">{DECISIONS.find((d) => d.value === doc.decision)?.label || doc.decision}</Badge>}
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="max-h-80 overflow-auto rounded border border-slate-100">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 sticky top-0 bg-white"><tr><th className="text-left">Artigo</th><th className="text-right">Qtd</th><th className="text-left pl-4">Loja na linha</th></tr></thead>
            <tbody>
              {(doc.document?.lines || []).map((ln) => (
                <tr key={ln.line_number} className={ln.store_code !== doc.store_code ? 'bg-amber-50' : ''}>
                  <td className="font-mono">{ln.sku}</td><td className="text-right">{ln.quantity}</td><td className="pl-4">{ln.store_code}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {doc.status === 'PENDING' ? <DecisionForm doc={doc} onDone={onDone} /> : (
            <p className="text-xs text-slate-500" data-testid="quarantine-decided-info">
              Decidido por {doc.decided_by} em {new Date(doc.decided_at).toLocaleString('pt-PT')} — {doc.decision_reason}
              {doc.batch_ids?.length > 0 && <> · Movimento: <span className="font-mono">{doc.batch_ids.join(', ')}</span></>}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export const QuarantineDocuments = ({ onCountChange }) => {
  const { getStoreName } = useStoreMapping();
  const [data, setData] = useState({ documents: [], pending: 0 });
  const [status, setStatus] = useState('PENDING');
  const load = useCallback(async () => {
    const res = await axios.get(`${API}/wintouch/quarantine`, { params: status ? { status } : {} });
    setData(res.data);
    onCountChange?.(res.data.pending);
  }, [status, onCountChange]);
  useEffect(() => { load().catch(() => toast.error('Erro ao carregar documentos por validar')); }, [load]);

  return (
    <div className="space-y-4" data-testid="quarantine-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2 max-w-3xl">
          <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
          <p className="text-sm text-slate-600">Documentos com linhas de stock de outra loja. O resto do dia foi integrado normalmente; estes documentos não mexem em stock até decidir. Cada decisão cria um movimento próprio para o SAGE e fica registada.</p>
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="quarantine-status-filter">
          <option value="PENDING">Por decidir</option>
          <option value="DECIDED">Decididos</option>
          <option value="">Todos</option>
        </select>
      </div>
      {data.documents.map((d) => <QuarantineRow key={d.document_key} doc={d} onDone={load} getStoreName={getStoreName} />)}
      {!data.documents.length && <div className="p-8 text-center text-slate-400 border rounded-lg" data-testid="quarantine-empty">Sem documentos {status === 'PENDING' ? 'por decidir' : ''}.</div>}
    </div>
  );
};

export default QuarantineDocuments;
